import update from "immutability-helper";
import React from "react";
import { type TemplateAndFormItem } from "../../advancing/model";
import { isReadonlyChunk } from "../common";
import { type FormItemSelection } from "../model";
import TemplateEditItem from "./TemplateEditItem";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";

interface Props {
  section: server.dto.AdvancingFormSection
  templateItems: server.dto.AdvancingTemplateItem[]
  onChange: (template: server.dto.AdvancingTemplateItem, newValue: FormItemSelection) => void
  onSectionTypeChange: (section: server.dto.AdvancingFormSection, newValue: FormItemSelection) => void
}

export default class TemplateEditSection extends React.Component<Props> {
  onSectionChange = (items: TemplateAndFormItem[], type: FormItemSelection) => {
    items.forEach(item => {
      if (isReadonlyChunk(item.formItem.chunkId, item.formItem.type.value) && (type === "optional" || type === "required")) {
        return;
      }

      let templateItem = item.templateItem || {
        chunkId: item.formItem.chunkId,
        readOnly: false,
        required: false
      };

      templateItem = update(templateItem, {
        readOnly: { $set: type === "readonly" },
        required: { $set: type === "required" }
      });

      this.props.onChange(templateItem, type);
    });
  };

  onCheck = (checkedValue: FormItemSelection) => {
    this.props.onSectionTypeChange(this.props.section, checkedValue);
  };

  public render(): JSX.Element {
    const { section, templateItems } = this.props;
    // const sectionGroupname = "group-" + section.label + section.formItems.length;

    const items: TemplateAndFormItem[] = section.formItems.map(sectionItem => {
      const templateItem = templateItems.find(tItem => tItem.chunkId === sectionItem.chunkId);
      return {
        templateItem,
        formItem: sectionItem
      };
    });

    // We need to decide if any of the section-wide types are selected
    // So if all formitems are set to "hidden", it should check the "hidden" radio button at section label
    let allFormItemsOfType: FormItemSelection | undefined;

    const containsNoFormItems = items.length > 0 && items.filter(i => i.templateItem != null).length === 0;
    if (containsNoFormItems) {
      allFormItemsOfType = "hidden";
    } else if (items.length > 0) {
      const radioResult = items.reduce((result, current) => {
        const chunkIsReadonly = isReadonlyChunk(current.formItem.chunkId, current.formItem.type.value);

        // One is hidden, so it can't be readonly only, but it can still be the others if the chunk is readonly
        if (!current.templateItem) {
          return {
            allAreReadonly: false,
            allAreRequired: chunkIsReadonly ? result.allAreRequired : false,
            allAreOptional: chunkIsReadonly ? result.allAreOptional : false
          };
        }

        const isReadonly = current.templateItem.readOnly;
        const isRequired = current.templateItem.required;

        // Lets say the form item is readonly, that means all the other options are DQ'd
        // But the readonly field will remain as it is
        return {
          allAreRequired: (isRequired || chunkIsReadonly) ? result.allAreRequired : false,
          allAreReadonly: isReadonly ? result.allAreReadonly : false,
          allAreOptional: ((!isRequired && !isReadonly) || (chunkIsReadonly)) ? result.allAreOptional : false
        };
      }, {
        allAreReadonly: true,
        allAreRequired: true,
        allAreOptional: true
      });

      // This happens if only readonly chunks were present
      if (radioResult.allAreRequired && radioResult.allAreOptional) {
        allFormItemsOfType = radioResult.allAreReadonly ? "readonly" : undefined;
      } else if (radioResult.allAreOptional) {
        allFormItemsOfType = "optional";
      } else if (radioResult.allAreReadonly) {
        allFormItemsOfType = "readonly";
      } else if (radioResult.allAreRequired) {
        allFormItemsOfType = "required";
      }
    }

    return <>
      <h2 className="advancing-template-header-row">{section.label}</h2>
      <RadioGroup
        wrapperClassName="advancing-template-header-row-radio"
        orientation="horizontal"
        options={[{
          label: RESX.Advancing.Hidden,
          value: "hidden"
        }, {
          label: RESX.Advancing.ReadOnly,
          value: "readonly"
        }, {
          label: RESX.Advancing.Optional,
          value: "optional"
        }, {
          label: RESX.Advancing.Required,
          value: "required"
        }]}
        value={allFormItemsOfType}
        onChange={value => this.onCheck(value)}
      />
      {
        items.map(item => (
          <TemplateEditItem key={item.formItem.chunkId} formItem={item.formItem} templateItem={item.templateItem} onChange={this.props.onChange} />
        ))
      }
    </>;
  }
}
